/**
 * @fileoverview Session-persistent countdown hook with utility functions
 *
 * This module provides a React hook for creating countdown timers that can persist
 * their state across browser sessions using session storage. It's particularly useful
 * for scenarios like rate limiting, cooldown periods, or temporary blocks where you
 * want the countdown to continue even if the user closes and reopens the browser tab.
 *
 * Key features:
 * - Session storage persistence across browser sessions
 * - Automatic sync when browser tab becomes visible
 * - Formatted time display (HH:MM:SS or MM:SS)
 * - Programmatic control (start, stop, reset)
 * - Callback support for completion events
 * - Auto-start capability
 *
 * @author MekiDoc Team
 * @version 1.0.0
 */

import { useState, useEffect, useRef, useCallback } from "react";

/**
 * Configuration options for the session-persistent countdown hook.
 */
interface UseCountdownOptions {
  /** Initial countdown time in seconds */
  initialTimeInSeconds: number;
  /** Callback function to execute when countdown reaches zero */
  onComplete?: () => void;
  /** Whether to automatically start the countdown when the hook initializes */
  autoStart?: boolean;
  /** Session storage key for persisting countdown state across browser sessions */
  sessionStorageKey?: string;
}

/**
 * Return object from the session-persistent countdown hook.
 */
interface UseCountdownReturn {
  /** Remaining time in seconds */
  timeLeft: number;
  /** Whether the countdown is currently active */
  isActive: boolean;
  /** Function to start the countdown */
  start: () => void;
  /** Function to clear/reset the countdown */
  clear: () => void;
  /** Formatted time string (HH:MM:SS or MM:SS) */
  formattedTime: string;
  /** Function to set an unblock timestamp in session storage */
  setUnblockTimestamp: (timestamp: number) => void;
  /** Function to clear the unblock timestamp from session storage */
  clearUnblockTimestamp: () => void;
}

/**
 * Stores an unblock timestamp in session storage for persistence across browser sessions.
 *
 * @param key - The session storage key to store the timestamp under
 * @param unblockTimestamp - The timestamp (in milliseconds) when the countdown should complete
 *
 * @example
 * ```typescript
 * storeUnblockTimestamp("countdown-123", Date.now() + 300000); // 5 minutes from now
 * ```
 */
export function storeUnblockTimestamp(
  key: string,
  unblockTimestamp: number
): void {
  try {
    sessionStorage.setItem(key, unblockTimestamp.toString());
  } catch (error) {
    console.warn("Failed to store unblock timestamp in sessionStorage:", error);
  }
}

/**
 * Retrieves an unblock timestamp from session storage.
 *
 * @param key - The session storage key to retrieve the timestamp from
 * @returns The stored timestamp in milliseconds, or null if not found or on error
 *
 * @example
 * ```typescript
 * const timestamp = getUnblockTimestamp("countdown-123");
 * if (timestamp) {
 *   const remainingTime = Math.max(0, timestamp - Date.now());
 * }
 * ```
 */
export function getUnblockTimestamp(key: string): number | null {
  try {
    const stored = sessionStorage.getItem(key);
    return stored ? parseInt(stored, 10) : null;
  } catch (error) {
    console.warn("Failed to get unblock timestamp from sessionStorage:", error);
    return null;
  }
}

/**
 * Clears an unblock timestamp from session storage.
 *
 * @param key - The session storage key to clear
 *
 * @example
 * ```typescript
 * clearUnblockTimestamp("countdown-123");
 * ```
 */
export function clearUnblockTimestamp(key: string): void {
  try {
    sessionStorage.removeItem(key);
  } catch (error) {
    console.warn(
      "Failed to clear unblock timestamp from sessionStorage:",
      error
    );
  }
}

/**
 * A React hook that provides a countdown timer with session storage persistence.
 *
 * This hook creates a countdown timer that can persist its state across browser sessions
 * using session storage. It automatically syncs when the browser tab becomes visible again
 * and provides both formatted time display and programmatic control.
 *
 * @param options - Configuration options for the countdown
 * @returns Object containing countdown state and control functions
 *
 * @example
 * Basic usage without persistence:
 * ```typescript
 * const { timeLeft, isActive, start, formattedTime } = useSessionPersistentCountdown({
 *   initialTimeInSeconds: 300, // 5 minutes
 *   onComplete: () => console.log("Countdown finished!"),
 *   autoStart: false
 * });
 * ```
 *
 * @example
 * With session storage persistence:
 * ```typescript
 * const { timeLeft, isActive, start, setUnblockTimestamp } = useSessionPersistentCountdown({
 *   initialTimeInSeconds: 600, // 10 minutes
 *   sessionStorageKey: "my-countdown",
 *   onComplete: () => {
 *     // Handle completion
 *   }
 * });
 *
 * // Set a specific unblock time (e.g., 10 minutes from now)
 * setUnblockTimestamp(Date.now() + 600000);
 * ```
 *
 * @example
 * Auto-starting countdown:
 * ```typescript
 * const { formattedTime, clear } = useSessionPersistentCountdown({
 *   initialTimeInSeconds: 120, // 2 minutes
 *   autoStart: true,
 *   onComplete: () => {
 *     // Auto-clear after 2 minutes
 *   }
 * });
 * ```
 */
export function useSessionPersistentCountdown({
  initialTimeInSeconds,
  onComplete,
  autoStart = false,
  sessionStorageKey,
}: UseCountdownOptions): UseCountdownReturn {
  const [timeLeft, setTimeLeft] = useState(initialTimeInSeconds);
  const [isActive, setIsActive] = useState(autoStart);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const startTimeRef = useRef<Date | null>(null);
  const initialTimeRef = useRef<number>(initialTimeInSeconds);

  // Update initial time reference when prop changes
  useEffect(() => {
    initialTimeRef.current = initialTimeInSeconds;
  }, [initialTimeInSeconds]);

  /**
   * Formats time in seconds to a human-readable string.
   * Returns HH:MM:SS format for times over 1 hour, MM:SS otherwise.
   *
   * @param seconds - Time in seconds to format
   * @returns Formatted time string
   */
  const formatTime = useCallback((seconds: number): string => {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const remainingSeconds = seconds % 60;

    if (hours > 0) {
      return `${hours.toString().padStart(2, "0")}:${minutes
        .toString()
        .padStart(2, "0")}:${remainingSeconds.toString().padStart(2, "0")}`;
    }
    return `${minutes.toString().padStart(2, "0")}:${remainingSeconds
      .toString()
      .padStart(2, "0")}`;
  }, []);

  /**
   * Calculates the elapsed time since the countdown started.
   *
   * @returns Elapsed time in seconds, or 0 if countdown hasn't started
   */
  const calculateElapsedTime = useCallback((): number => {
    if (!startTimeRef.current) return 0;
    const now = new Date();
    return Math.floor((now.getTime() - startTimeRef.current.getTime()) / 1000);
  }, []);

  /**
   * Calculates remaining time based on stored unblock timestamp in session storage.
   *
   * @returns Remaining time in seconds, or 0 if no valid timestamp found
   */
  const calculateTimeLeftFromStorage = useCallback((): number => {
    if (!sessionStorageKey) return 0;

    const unblockTimestamp = getUnblockTimestamp(sessionStorageKey);
    if (!unblockTimestamp) return 0;

    const now = Date.now();
    const remaining = Math.max(0, Math.floor((unblockTimestamp - now) / 1000));

    return remaining;
  }, [sessionStorageKey]);

  // Initialize countdown from sessionStorage if available
  useEffect(() => {
    if (sessionStorageKey) {
      const storedTimeLeft = calculateTimeLeftFromStorage();
      if (storedTimeLeft > 0) {
        setTimeLeft(storedTimeLeft);
        setIsActive(true);
      }
    }
  }, [sessionStorageKey, calculateTimeLeftFromStorage]);

  /**
   * Updates the remaining time based on current state and storage.
   * Handles both session storage and regular elapsed time calculations.
   */
  const updateTimeLeft = useCallback(() => {
    if (!isActive) return;

    let remaining: number;

    if (sessionStorageKey) {
      // Use sessionStorage timestamp if available
      remaining = calculateTimeLeftFromStorage();

      // If countdown is complete based on storage, clear it and stop
      if (remaining <= 0) {
        clearUnblockTimestamp(sessionStorageKey);
        setIsActive(false);
        onComplete?.();
        return;
      }
    } else {
      // Fallback to regular elapsed time calculation
      if (!startTimeRef.current) return;
      const elapsed = calculateElapsedTime();
      remaining = Math.max(0, initialTimeRef.current - elapsed);

      if (remaining <= 0) {
        setIsActive(false);
        onComplete?.();
        return;
      }
    }

    setTimeLeft(remaining);
  }, [
    isActive,
    sessionStorageKey,
    calculateTimeLeftFromStorage,
    calculateElapsedTime,
    onComplete,
  ]);

  /**
   * Starts the countdown timer.
   * Sets the start time reference and activates the countdown.
   */
  const start = useCallback(() => {
    setIsActive(true);
    startTimeRef.current = new Date();
  }, []);

  /**
   * Sets an unblock timestamp in session storage.
   * This allows the countdown to persist across browser sessions.
   *
   * @param timestamp - The timestamp (in milliseconds) when the countdown should complete
   */
  const setUnblockTimestamp = useCallback(
    (timestamp: number) => {
      if (sessionStorageKey) {
        storeUnblockTimestamp(sessionStorageKey, timestamp);
      }
    },
    [sessionStorageKey]
  );

  /**
   * Clears the unblock timestamp from session storage.
   * This stops the countdown persistence across browser sessions.
   */
  const clearUnblockTimestampLocal = useCallback(() => {
    if (sessionStorageKey) {
      clearUnblockTimestamp(sessionStorageKey);
    }
  }, [sessionStorageKey]);

  /**
   * Clears and resets the countdown to its initial state.
   * Stops the timer, resets time to initial value, and clears session storage.
   */
  const clear = useCallback(() => {
    setIsActive(false);
    setTimeLeft(initialTimeInSeconds);
    initialTimeRef.current = initialTimeInSeconds;
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
    startTimeRef.current = null;

    // Clear sessionStorage if using it
    if (sessionStorageKey) {
      clearUnblockTimestamp(sessionStorageKey);
    }
  }, [initialTimeInSeconds, sessionStorageKey]);

  // Handle visibility change to sync countdown when tab becomes visible
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible" && isActive) {
        updateTimeLeft();
      }
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);

    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [isActive, updateTimeLeft]);

  // Main countdown effect
  useEffect(() => {
    if (isActive) {
      // Update immediately when starting
      updateTimeLeft();

      intervalRef.current = setInterval(() => {
        updateTimeLeft();
      }, 1000);
    } else {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    }

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, [isActive, updateTimeLeft]);

  return {
    timeLeft,
    isActive,
    start,
    clear,
    formattedTime: formatTime(timeLeft),
    setUnblockTimestamp,
    clearUnblockTimestamp: clearUnblockTimestampLocal,
  };
}
